import React, { useState, useTransition, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { Trophy, Users, ListChecks, Award, Menu, X, LogOut, Home, Calendar, UserCog, Building2, Settings, FileText, Medal, Database, LayoutDashboard, QrCode, ClipboardList, UserPlus, Link2, Upload, History } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import FullscreenToggle from './FullscreenToggle';
import { supabase } from '../../lib/supabase';

const Navbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { user, logout } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [isReferee, setIsReferee] = useState(false);
  const [isViewer, setIsViewer] = useState(false);
  const [isRegistration, setIsRegistration] = useState(false);
  const [showDropdown, setShowDropdown] = useState<string | null>(null);
  const [pendingRegistrationsCount, setPendingRegistrationsCount] = useState(0);

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  useEffect(() => {
    const checkUserRoles = async () => {
      if (!user?.id) {
        setIsAdmin(false);
        setIsManager(false);
        setIsReferee(false);
        setIsViewer(false);
        setIsRegistration(false);
        return;
      }

      try {
        const { data: userRoles, error } = await supabase
          .from('user_roles')
          .select(`
            role_id,
            roles!user_roles_role_id_fkey (
              role_name
            )
          `)
          .eq('user_id', user.id);

        if (error) {
          console.error('Error fetching user roles:', error);
          // Clear role states on error
          setIsAdmin(false);
          setIsManager(false);
          setIsReferee(false);
          setIsViewer(false);
          setIsRegistration(false);
          return;
        }

        if (userRoles) {
          const roleNames = userRoles.map(ur => ur.roles?.role_name);
          setIsAdmin(roleNames.includes('admin'));
          setIsManager(roleNames.includes('manager'));
          setIsReferee(roleNames.includes('referee'));
          setIsViewer(roleNames.includes('viewer'));
          setIsRegistration(roleNames.includes('registrering'));
        }
      } catch (error) {
        console.error('Error checking user roles:', error);
        // Clear role states on error
        setIsAdmin(false);
        setIsManager(false);
        setIsReferee(false);
        setIsViewer(false);
        setIsRegistration(false);
      }
    };

    // Only check roles if we have a valid user session
    if (user?.id) {
      checkUserRoles();
    } else {
      // Clear role states if no user
      setIsAdmin(false);
      setIsManager(false);
      setIsReferee(false);
      setIsViewer(false);
      setIsRegistration(false);
    }
  }, [user?.id]);

  // Check for pending registrations
  useEffect(() => {
    const fetchPendingRegistrations = async () => {
      if (isAdmin || isManager) {
        try {
          const { count, error } = await supabase
            .from('pending_registrations')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'pending');
          
          if (error) throw error;
          
          setPendingRegistrationsCount(count || 0);
        } catch (error) {
          console.error('Error fetching pending registrations count:', error);
        }
      }
    };

    fetchPendingRegistrations();

    // Set up a subscription for real-time updates
    if (isAdmin || isManager) {
      const subscription = supabase
        .channel('pending_registrations_changes')
        .on('postgres_changes', { 
          event: '*', 
          schema: 'public', 
          table: 'pending_registrations' 
        }, () => {
          fetchPendingRegistrations();
        })
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [isAdmin, isManager]);

  // Redirect referee to results page if they try to access other pages
  useEffect(() => {
    if (isReferee && !isAdmin && !isManager && !isViewer && !isRegistration && location.pathname !== '/competitions/results' && location.pathname !== '/login') {
      navigate('/competitions/results');
    }
  }, [isReferee, isAdmin, isManager, isViewer, isRegistration, location.pathname, navigate]);

  const handleLogout = async () => {
    // Only call logout if there's actually a user session to logout
    if (user) {
      await logout();
    }
    startTransition(() => {
      navigate('/login');
    });
  };

  const handleMobileMenuClick = (callback: () => void) => {
    startTransition(() => {
      setIsOpen(false);
      callback();
    });
  };

  const toggleMobileMenu = () => {
    startTransition(() => {
      setIsOpen(!isOpen);
    });
  };

  const toggleDropdown = (id: string) => {
    setShowDropdown(prev => prev === id ? null : id);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowDropdown(null);
    };
    
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Prevent dropdown toggle from triggering the outside click handler
  const handleDropdownToggle = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    toggleDropdown(id);
  };

  const renderRefereeNav = () => (
    <nav className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-2">
            <Trophy className="h-8 w-8" />
            <span className="text-xl font-bold">Annai Sports</span>
          </div>
          <div className="flex items-center space-x-4">
            <FullscreenToggle />
            <LanguageSwitcher />
            <button 
              onClick={handleLogout}
              className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-white hover:bg-blue-500 transition-colors duration-150 ease-in-out"
              disabled={isPending}
            >
              <LogOut className="h-5 w-5 mr-2" />
              {t('nav.logout')}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );

  const renderViewerNav = () => (
    <nav className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-2">
            <Trophy className="h-8 w-8" />
            <Link to="/" className="text-xl font-bold">Annai Sports</Link>
          </div>
          
          {/* Desktop Navigation for Viewer */}
          <div className="hidden md:flex items-center space-x-4">
            {user && (
              <>
                <Link
                  to="/participants"
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out
                    ${isActivePath('/participants') 
                      ? 'bg-white text-blue-700' 
                      : 'text-white hover:bg-blue-500'}`}
                >
                  <Users className="h-5 w-5 mr-2" />
                  {t('nav.participants')}
                </Link>

                <Link
                  to="/competitions"
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out
                    ${isActivePath('/competitions') 
                      ? 'bg-white text-blue-700' 
                      : 'text-white hover:bg-blue-500'}`}
                >
                  <ListChecks className="h-5 w-5 mr-2" />
                  {t('nav.competitions')}
                </Link>
                
                <Link
                  to="/champions"
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out
                    ${isActivePath('/champions') 
                      ? 'bg-white text-blue-700' 
                      : 'text-white hover:bg-blue-500'}`}
                >
                  <Trophy className="h-5 w-5 mr-2" />
                  {t('nav.champions')}
                </Link>
                
                <Link
                  to="/houses/performance"
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out
                    ${isActivePath('/houses/performance') 
                      ? 'bg-white text-blue-700' 
                      : 'text-white hover:bg-blue-500'}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LayoutDashboard className="h-5 w-5 mr-2" />
                  {t('nav.housePerformanceDisplay')}
                </Link>
                
                <button 
                  onClick={handleLogout}
                  className="flex items-center ml-4 px-3 py-2 rounded-md text-sm font-medium text-white hover:bg-blue-500 transition-colors duration-150 ease-in-out"
                  disabled={isPending}
                >
                  <LogOut className="h-5 w-5 mr-2" />
                  {t('nav.logout')}
                </button>
              </>
            )}
            <FullscreenToggle />
            <LanguageSwitcher />
          </div>
          
          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-4">
            <FullscreenToggle />
            <LanguageSwitcher />
            <button
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-white hover:bg-blue-500 focus:outline-none transition-colors duration-150 ease-in-out"
            >
              {isOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu for Viewer */}
      {isOpen && (
        <div className="md:hidden bg-blue-700 px-2 pt-2 pb-4 space-y-1 sm:px-3">
          {user && (
            <>
              <Link
                to="/participants"
                className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                  isActivePath('/participants') 
                    ? 'bg-white text-blue-700' 
                    : 'text-white hover:bg-blue-500'
                }`}
                onClick={() => handleMobileMenuClick(() => {})}
              >
                <Users className="h-5 w-5 mr-2" />
                {t('nav.participants')}
              </Link>

              <Link
                to="/competitions"
                className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                  isActivePath('/competitions') 
                    ? 'bg-white text-blue-700' 
                    : 'text-white hover:bg-blue-500'
                }`}
                onClick={() => handleMobileMenuClick(() => {})}
              >
                <ListChecks className="h-5 w-5 mr-2" />
                {t('nav.competitions')}
              </Link>
              
              <Link
                to="/champions"
                className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                  isActivePath('/champions') 
                    ? 'bg-white text-blue-700' 
                    : 'text-white hover:bg-blue-500'
                }`}
                onClick={() => handleMobileMenuClick(() => {})}
              >
                <Trophy className="h-5 w-5 mr-2" />
                {t('nav.champions')}
              </Link>
              
              <Link
                to="/houses/performance"
                className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                  isActivePath('/houses/performance') 
                    ? 'bg-white text-blue-700' 
                    : 'text-white hover:bg-blue-500'
                }`}
                onClick={() => handleMobileMenuClick(() => {})}
                target="_blank"
                rel="noopener noreferrer"
              >
                <LayoutDashboard className="h-5 w-5 mr-2" />
                {t('nav.housePerformanceDisplay')}
              </Link>
              
              <button 
                onClick={() => handleMobileMenuClick(handleLogout)}
                className="flex w-full items-center px-3 py-2 rounded-md text-base font-medium text-white hover:bg-blue-500"
                disabled={isPending}
              >
                <LogOut className="h-5 w-5 mr-2" />
                {t('nav.logout')}
              </button>
            </>
          )}
          {!user && (
            <Link
              to="/login"
              className="block px-3 py-2 rounded-md text-base font-medium bg-white text-blue-700 hover:bg-gray-100"
              onClick={() => handleMobileMenuClick(() => {})}
            >
              {t('nav.login')}
            </Link>
          )}
        </div>
      )}
    </nav>
  );

  const renderRegistrationNav = () => (
    <nav className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-2">
            <Trophy className="h-8 w-8" />
            <Link to="/" className="text-xl font-bold">Annai Sports</Link>
          </div>
          
          {/* Desktop Navigation for Registration */}
          <div className="hidden md:flex items-center space-x-4">
            {user && (
              <>
                <Link
                  to="/participants"
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out
                    ${isActivePath('/participants') 
                      ? 'bg-white text-blue-700' 
                      : 'text-white hover:bg-blue-500'}`}
                >
                  <Users className="h-5 w-5 mr-2" />
                  {t('nav.participants')}
                </Link>

                <Link
                  to="/participants/family-connections"
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out
                    ${isActivePath('/participants/family-connections') 
                      ? 'bg-white text-blue-700' 
                      : 'text-white hover:bg-blue-500'}`}
                >
                  <Link2 className="h-5 w-5 mr-2" />
                  {t('nav.familyConnections')}
                </Link>
                
                <button 
                  onClick={handleLogout}
                  className="flex items-center ml-4 px-3 py-2 rounded-md text-sm font-medium text-white hover:bg-blue-500 transition-colors duration-150 ease-in-out"
                  disabled={isPending}
                >
                  <LogOut className="h-5 w-5 mr-2" />
                  {t('nav.logout')}
                </button>
              </>
            )}
            <FullscreenToggle />
            <LanguageSwitcher />
          </div>
          
          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-4">
            <FullscreenToggle />
            <LanguageSwitcher />
            <button
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-white hover:bg-blue-500 focus:outline-none transition-colors duration-150 ease-in-out"
            >
              {isOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu for Registration */}
      {isOpen && (
        <div className="md:hidden bg-blue-700 px-2 pt-2 pb-4 space-y-1 sm:px-3">
          {user && (
            <>
              <Link
                to="/participants"
                className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                  isActivePath('/participants') 
                    ? 'bg-white text-blue-700' 
                    : 'text-white hover:bg-blue-500'
                }`}
                onClick={() => handleMobileMenuClick(() => {})}
              >
                <Users className="h-5 w-5 mr-2" />
                {t('nav.participants')}
              </Link>

              <Link
                to="/participants/family-connections"
                className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                  isActivePath('/participants/family-connections') 
                    ? 'bg-white text-blue-700' 
                    : 'text-white hover:bg-blue-500'
                }`}
                onClick={() => handleMobileMenuClick(() => {})}
              >
                <Link2 className="h-5 w-5 mr-2" />
                {t('nav.familyConnections')}
              </Link>
              
              <button 
                onClick={() => handleMobileMenuClick(handleLogout)}
                className="flex w-full items-center px-3 py-2 rounded-md text-base font-medium text-white hover:bg-blue-500"
                disabled={isPending}
              >
                <LogOut className="h-5 w-5 mr-2" />
                {t('nav.logout')}
              </button>
            </>
          )}
          {!user && (
            <Link
              to="/login"
              className="block px-3 py-2 rounded-md text-base font-medium bg-white text-blue-700 hover:bg-gray-100"
              onClick={() => handleMobileMenuClick(() => {})}
            >
              {t('nav.login')}
            </Link>
          )}
        </div>
      )}
    </nav>
  );

  const renderFullNav = () => (
    <nav className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-2">
            <Trophy className="h-8 w-8" />
            <Link to="/" className="text-xl font-bold">Annai Sports</Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {user && (
              <>
                <Link
                  to="/participants"
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out
                    ${isActivePath('/participants') 
                      ? 'bg-white text-blue-700' 
                      : 'text-white hover:bg-blue-500'}`}
                >
                  <Users className="h-5 w-5 mr-2" />
                  {t('nav.participants')}
                </Link>

                <Link
                  to="/competitions"
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out
                    ${isActivePath('/competitions') 
                      ? 'bg-white text-blue-700' 
                      : 'text-white hover:bg-blue-500'}`}
                >
                  <ListChecks className="h-5 w-5 mr-2" />
                  {t('nav.competitions')}
                </Link>

                {(isReferee || isAdmin || isManager) && (
                  <Link
                    to="/competitions/results"
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out
                      ${isActivePath('/competitions/results') 
                        ? 'bg-white text-blue-700' 
                        : 'text-white hover:bg-blue-500'}`}
                  >
                    <Medal className="h-5 w-5 mr-2" />
                    {t('mobileResults.title')}
                  </Link>
                )}

                {(isAdmin || isManager) && (
                  <Link
                    to="/competitions/admin"
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out
                      ${isActivePath('/competitions/admin') 
                        ? 'bg-white text-blue-700' 
                        : 'text-white hover:bg-blue-500'}`}
                  >
                    <Database className="h-5 w-5 mr-2" />
                    {t('nav.adminView')}
                  </Link>
                )}
                
                {/* Management Dropdown */}
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={(e) => handleDropdownToggle(e, 'management')}
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out
                      ${showDropdown === 'management' 
                        ? 'bg-white text-blue-700' 
                        : 'text-white hover:bg-blue-500'}`}
                  >
                    <Settings className="h-5 w-5 mr-2" />
                    {t('nav.management')}
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </button>
                  
                  {showDropdown === 'management' && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50">
                      <div className="py-1 rounded-md bg-white shadow-xs">
                        <Link
                          to="/houses"
                          className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
                            isActivePath('/houses') ? 'bg-gray-100' : ''
                          }`}
                          onClick={() => setShowDropdown(null)}
                        >
                          <div className="flex items-center">
                            <Home className="h-5 w-5 mr-2" />
                            {t('nav.houses')}
                          </div>
                        </Link>
                        <Link
                          to="/departments"
                          className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
                            isActivePath('/departments') ? 'bg-gray-100' : ''
                          }`}
                          onClick={() => setShowDropdown(null)}
                        >
                          <div className="flex items-center">
                            <Building2 className="h-5 w-5 mr-2" />
                            {t('nav.departments')}
                          </div>
                        </Link>
                        <Link
                          to="/age-groups"
                          className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
                            isActivePath('/age-groups') ? 'bg-gray-100' : ''
                          }`}
                          onClick={() => setShowDropdown(null)}
                        >
                          <div className="flex items-center">
                            <Calendar className="h-5 w-5 mr-2" />
                            {t('nav.ageGroups')}
                          </div>
                        </Link>
                        <Link
                          to="/referees"
                          className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
                            isActivePath('/referees') ? 'bg-gray-100' : ''
                          }`}
                          onClick={() => setShowDropdown(null)}
                        >
                          <div className="flex items-center">
                            <UserCog className="h-5 w-5 mr-2" />
                            {t('nav.referees')}
                          </div>
                        </Link>
                        <Link
                          to="/champions"
                          className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
                            isActivePath('/champions') ? 'bg-gray-100' : ''
                          }`}
                          onClick={() => setShowDropdown(null)}
                        >
                          <div className="flex items-center">
                            <Trophy className="h-5 w-5 mr-2" />
                            {t('nav.champions')}
                          </div>
                        </Link>
                        <Link
                          to="/houses/performance"
                          className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
                            isActivePath('/houses/performance') ? 'bg-gray-100' : ''
                          }`}
                          onClick={() => setShowDropdown(null)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <div className="flex items-center">
                            <LayoutDashboard className="h-5 w-5 mr-2" />
                            {t('nav.housePerformanceDisplay')}
                          </div>
                        </Link>
                        <Link
                          to="/participants/cards"
                          className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
                            isActivePath('/participants/cards') ? 'bg-gray-100' : ''
                          }`}
                          onClick={() => setShowDropdown(null)}
                        >
                          <div className="flex items-center">
                            <QrCode className="h-5 w-5 mr-2" />
                            {t('participants.cards.title')}
                          </div>
                        </Link>
                        <Link
                          to="/referees/cards"
                          className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
                            isActivePath('/referees/cards') ? 'bg-gray-100' : ''
                          }`}
                          onClick={() => setShowDropdown(null)}
                        >
                          <div className="flex items-center">
                            <QrCode className="h-5 w-5 mr-2" />
                            {t('referees.cards.title')}
                          </div>
                        </Link>
                        <Link
                          to="/participants/family-connections"
                          className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
                            isActivePath('/participants/family-connections') ? 'bg-gray-100' : ''
                          }`}
                          onClick={() => setShowDropdown(null)}
                        >
                          <div className="flex items-center">
                            <Link2 className="h-5 w-5 mr-2" />
                            {t('nav.familyConnections')}
                          </div>
                        </Link>
                        {(isAdmin || isManager) && (
                          <Link
                            to="/admin/medal-requirements"
                            className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
                              isActivePath('/admin/medal-requirements') ? 'bg-gray-100' : ''
                            }`}
                            onClick={() => setShowDropdown(null)}
                          >
                            <div className="flex items-center">
                              <Medal className="h-5 w-5 mr-2" />
                              {t('nav.medalRequirements')}
                            </div>
                          </Link>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Admin Links */}
                {(isAdmin || isManager) && (
                  <Link
                    to="/reports"
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out
                      ${isActivePath('/reports') 
                        ? 'bg-white text-blue-700' 
                        : 'text-white hover:bg-blue-500'}`}
                  >
                    <FileText className="h-5 w-5 mr-2" />
                    {t('nav.reports')}
                  </Link>
                )}
                
                {isAdmin && (
                  <div className="relative" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => handleDropdownToggle(e, 'admin')}
                      className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out
                        ${showDropdown === 'admin' 
                          ? 'bg-white text-blue-700' 
                          : 'text-white hover:bg-blue-500'}`}
                    >
                      <Settings className="h-5 w-5 mr-2" />
                      Admin
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                      {pendingRegistrationsCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                          {pendingRegistrationsCount}
                        </span>
                      )}
                    </button>
                    
                    {showDropdown === 'admin' && (
                      <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg z-50">
                        <div className="py-1 rounded-md bg-white shadow-xs">
                          <Link
                            to="/admin/users"
                            className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
                              isActivePath('/admin/users') ? 'bg-gray-100' : ''
                            }`}
                            onClick={() => setShowDropdown(null)}
                          >
                            <div className="flex items-center">
                              <UserCog className="h-5 w-5 mr-2" />
                              {t('nav.userRoles')}
                            </div>
                          </Link>
                          <Link
                            to="/admin/settings"
                            className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
                              isActivePath('/admin/settings') ? 'bg-gray-100' : ''
                            }`}
                            onClick={() => setShowDropdown(null)}
                          >
                            <div className="flex items-center">
                              <Settings className="h-5 w-5 mr-2" />
                              System Settings
                            </div>
                          </Link>
                          <Link
                            to="/admin/logs"
                            className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
                              isActivePath('/admin/logs') ? 'bg-gray-100' : ''
                            }`}
                            onClick={() => setShowDropdown(null)}
                          >
                            <div className="flex items-center">
                              <History className="h-5 w-5 mr-2" />
                              System Logs
                            </div>
                          </Link>
                          <Link
                            to="/admin/pending-registrations"
                            className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
                              isActivePath('/admin/pending-registrations') ? 'bg-gray-100' : ''
                            }`}
                            onClick={() => setShowDropdown(null)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <ClipboardList className="h-5 w-5 mr-2" />
                                {t('nav.pendingRegistrations')}
                              </div>
                              {pendingRegistrationsCount > 0 && (
                                <span className="bg-red-100 text-red-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                                  {pendingRegistrationsCount}
                                </span>
                              )}
                            </div>
                          </Link>
                          <Link
                            to="/admin/registration-settings"
                            className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
                              isActivePath('/admin/registration-settings') ? 'bg-gray-100' : ''
                            }`}
                            onClick={() => setShowDropdown(null)}
                          >
                            <div className="flex items-center">
                              <UserPlus className="h-5 w-5 mr-2" />
                              {t('nav.registrationSettings')}
                            </div>
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                <button 
                  onClick={handleLogout}
                  className="flex items-center ml-4 px-3 py-2 rounded-md text-sm font-medium text-white hover:bg-blue-500 transition-colors duration-150 ease-in-out"
                  disabled={isPending}
                >
                  <LogOut className="h-5 w-5 mr-2" />
                  {t('nav.logout')}
                </button>
              </>
            )}
            {!user && (
              <Link
                to="/login"
                className="px-4 py-2 rounded-md text-sm font-medium bg-white text-blue-700 hover:bg-gray-100 transition-colors duration-150 ease-in-out"
              >
                {t('nav.login')}
              </Link>
            )}
            <FullscreenToggle />
            <LanguageSwitcher />
          </div>
          
          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-4">
            <FullscreenToggle />
            <LanguageSwitcher />
            <button
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-white hover:bg-blue-500 focus:outline-none transition-colors duration-150 ease-in-out"
            >
              {isOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-blue-700 px-2 pt-2 pb-4 space-y-1 sm:px-3">
          {user && (
            <>
              <Link
                to="/participants"
                className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                  isActivePath('/participants') 
                    ? 'bg-white text-blue-700' 
                    : 'text-white hover:bg-blue-500'
                }`}
                onClick={() => handleMobileMenuClick(() => {})}
              >
                <Users className="h-5 w-5 mr-2" />
                {t('nav.participants')}
              </Link>

              <Link
                to="/competitions"
                className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                  isActivePath('/competitions') 
                    ? 'bg-white text-blue-700' 
                    : 'text-white hover:bg-blue-500'
                }`}
                onClick={() => handleMobileMenuClick(() => {})}
              >
                <ListChecks className="h-5 w-5 mr-2" />
                {t('nav.competitions')}
              </Link>

              {(isReferee || isAdmin || isManager) && (
                <Link
                  to="/competitions/results"
                  className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                    isActivePath('/competitions/results') 
                      ? 'bg-white text-blue-700' 
                      : 'text-white hover:bg-blue-500'
                  }`}
                  onClick={() => handleMobileMenuClick(() => {})}
                >
                  <Medal className="h-5 w-5 mr-2" />
                  {t('mobileResults.title')}
                </Link>
              )}

              {(isAdmin || isManager) && (
                <Link
                  to="/competitions/admin"
                  className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                    isActivePath('/competitions/admin') 
                      ? 'bg-white text-blue-700' 
                      : 'text-white hover:bg-blue-500'
                  }`}
                  onClick={() => handleMobileMenuClick(() => {})}
                >
                  <Database className="h-5 w-5 mr-2" />
                  {t('nav.adminView')}
                </Link>
              )}
              
              {/* Mobile Management Section */}
              <div className="px-3 py-2">
                <div className="text-white font-medium mb-2">{t('nav.management')}</div>
                <div className="pl-2 space-y-1">
                  <Link
                    to="/houses"
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                      isActivePath('/houses') 
                        ? 'bg-white text-blue-700' 
                        : 'text-white hover:bg-blue-500'
                    }`}
                    onClick={() => handleMobileMenuClick(() => {})}
                  >
                    <Home className="h-5 w-5 mr-2" />
                    {t('nav.houses')}
                  </Link>
                  <Link
                    to="/departments"
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                      isActivePath('/departments') 
                        ? 'bg-white text-blue-700' 
                        : 'text-white hover:bg-blue-500'
                    }`}
                    onClick={() => handleMobileMenuClick(() => {})}
                  >
                    <Building2 className="h-5 w-5 mr-2" />
                    {t('nav.departments')}
                  </Link>
                  <Link
                    to="/age-groups"
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                      isActivePath('/age-groups') 
                        ? 'bg-white text-blue-700' 
                        : 'text-white hover:bg-blue-500'
                    }`}
                    onClick={() => handleMobileMenuClick(() => {})}
                  >
                    <Calendar className="h-5 w-5 mr-2" />
                    {t('nav.ageGroups')}
                  </Link>
                  <Link
                    to="/referees"
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                      isActivePath('/referees') 
                        ? 'bg-white text-blue-700' 
                        : 'text-white hover:bg-blue-500'
                    }`}
                    onClick={() => handleMobileMenuClick(() => {})}
                  >
                    <UserCog className="h-5 w-5 mr-2" />
                    {t('nav.referees')}
                  </Link>
                  <Link
                    to="/referees/import"
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                      isActivePath('/referees/import') 
                        ? 'bg-white text-blue-700' 
                        : 'text-white hover:bg-blue-500'
                    }`}
                    onClick={() => handleMobileMenuClick(() => {})}
                  >
                    <Upload className="h-5 w-5 mr-2" />
                    Import Referees
                  </Link>
                  <Link
                    to="/champions"
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                      isActivePath('/champions') 
                        ? 'bg-white text-blue-700' 
                        : 'text-white hover:bg-blue-500'
                    }`}
                    onClick={() => handleMobileMenuClick(() => {})}
                  >
                    <Trophy className="h-5 w-5 mr-2" />
                    {t('nav.champions')}
                  </Link>
                  <Link
                    to="/houses/performance"
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                      isActivePath('/houses/performance') 
                        ? 'bg-white text-blue-700' 
                        : 'text-white hover:bg-blue-500'
                    }`}
                    onClick={() => handleMobileMenuClick(() => {})}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <LayoutDashboard className="h-5 w-5 mr-2" />
                    {t('nav.housePerformanceDisplay')}
                  </Link>
                  <Link
                    to="/participants/cards"
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                      isActivePath('/participants/cards') 
                        ? 'bg-white text-blue-700' 
                        : 'text-white hover:bg-blue-500'
                    }`}
                    onClick={() => handleMobileMenuClick(() => {})}
                  >
                    <QrCode className="h-5 w-5 mr-2" />
                    {t('participants.cards.title')}
                  </Link>
                  <Link
                    to="/referees/cards"
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                      isActivePath('/referees/cards') 
                        ? 'bg-white text-blue-700' 
                        : 'text-white hover:bg-blue-500'
                    }`}
                    onClick={() => handleMobileMenuClick(() => {})}
                  >
                    <QrCode className="h-5 w-5 mr-2" />
                    {t('referees.cards.title')}
                  </Link>
                  <Link
                    to="/participants/family-connections"
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                      isActivePath('/participants/family-connections') 
                        ? 'bg-white text-blue-700' 
                        : 'text-white hover:bg-blue-500'
                    }`}
                    onClick={() => handleMobileMenuClick(() => {})}
                  >
                    <Link2 className="h-5 w-5 mr-2" />
                    Family Connections
                  </Link>
                  {(isAdmin || isManager) && (
                    <Link
                      to="/admin/medal-requirements"
                      className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                        isActivePath('/admin/medal-requirements') 
                          ? 'bg-white text-blue-700' 
                          : 'text-white hover:bg-blue-500'
                      }`}
                      onClick={() => handleMobileMenuClick(() => {})}
                    >
                      <Medal className="h-5 w-5 mr-2" />
                      Medal Requirements
                    </Link>
                  )}
                </div>
              </div>
              
              {/* Admin Links */}
              {(isAdmin || isManager) && (
                <Link
                  to="/reports"
                  className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                    isActivePath('/reports') 
                      ? 'bg-white text-blue-700' 
                      : 'text-white hover:bg-blue-500'
                  }`}
                  onClick={() => handleMobileMenuClick(() => {})}
                >
                  <FileText className="h-5 w-5 mr-2" />
                  {t('nav.reports')}
                </Link>
              )}
              
              {isAdmin && (
                <>
                  <Link
                    to="/admin/users"
                    className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                      isActivePath('/admin/users') 
                        ? 'bg-white text-blue-700' 
                        : 'text-white hover:bg-blue-500'
                    }`}
                    onClick={() => handleMobileMenuClick(() => {})}
                  >
                    <UserCog className="h-5 w-5 mr-2" />
                    {t('nav.userRoles')}
                  </Link>
                  <Link
                    to="/admin/settings"
                    className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                      isActivePath('/admin/settings') 
                        ? 'bg-white text-blue-700' 
                        : 'text-white hover:bg-blue-500'
                    }`}
                    onClick={() => handleMobileMenuClick(() => {})}
                  >
                    <Settings className="h-5 w-5 mr-2" />
                    System Settings
                  </Link>
                  <Link
                    to="/admin/logs"
                    className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                      isActivePath('/admin/logs') 
                        ? 'bg-white text-blue-700' 
                        : 'text-white hover:bg-blue-500'
                    }`}
                    onClick={() => handleMobileMenuClick(() => {})}
                  >
                    <History className="h-5 w-5 mr-2" />
                    System Logs
                  </Link>
                  <Link
                    to="/admin/pending-registrations"
                    className={`flex items-center justify-between px-3 py-2 rounded-md text-base font-medium ${
                      isActivePath('/admin/pending-registrations') 
                        ? 'bg-white text-blue-700' 
                        : 'text-white hover:bg-blue-500'
                    }`}
                    onClick={() => handleMobileMenuClick(() => {})}
                  >
                    <div className="flex items-center">
                      <ClipboardList className="h-5 w-5 mr-2" />
                      {t('nav.pendingRegistrations')}
                    </div>
                    {pendingRegistrationsCount > 0 && (
                      <span className="bg-red-100 text-red-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                        {pendingRegistrationsCount}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/admin/registration-settings"
                    className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                      isActivePath('/admin/registration-settings') 
                        ? 'bg-white text-blue-700' 
                        : 'text-white hover:bg-blue-500'
                    }`}
                    onClick={() => handleMobileMenuClick(() => {})}
                  >
                    <UserPlus className="h-5 w-5 mr-2" />
                    {t('nav.registrationSettings')}
                  </Link>
                </>
              )}
              
              <button 
                onClick={() => handleMobileMenuClick(handleLogout)}
                className="flex w-full items-center px-3 py-2 rounded-md text-base font-medium text-white hover:bg-blue-500"
                disabled={isPending}
              >
                <LogOut className="h-5 w-5 mr-2" />
                {t('nav.logout')}
              </button>
            </>
          )}
          {!user && (
            <Link
              to="/login"
              className="block px-3 py-2 rounded-md text-base font-medium bg-white text-blue-700 hover:bg-gray-100"
              onClick={() => handleMobileMenuClick(() => {})}
            >
              {t('nav.login')}
            </Link>
          )}
        </div>
      )}
    </nav>
  );

  // Return the appropriate navigation based on user role
  if (isReferee && !isAdmin && !isManager && !isViewer && !isRegistration) {
    return renderRefereeNav();
  } else if (isViewer && !isAdmin && !isManager && !isReferee && !isRegistration) {
    return renderViewerNav();
  } else if (isRegistration && !isAdmin && !isManager && !isReferee && !isViewer) {
    return renderRegistrationNav();
  } else {
    return renderFullNav();
  }
};

export default Navbar;